import { useTranslation } from 'react-i18next'

import {
  durationMinutes,
  formatDuration,
  localTimeOfInstant,
  type CalendarEvent,
} from '../../lib/calendar'

const COMPANY_TONE = 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
const PERSON_TONE = 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100'

export function tone(event: CalendarEvent): string {
  return event.source === 'company' ? COMPANY_TONE : PERSON_TONE
}

export function DayEventList({ events, offset }: { events: CalendarEvent[]; offset: number }) {
  const { t } = useTranslation()
  return (
    <ul className="space-y-2">
      {events.map((e) => {
        const minutes = durationMinutes(e)
        return (
          <li key={`${e.source}-${e.id}`} className="rounded-lg border border-border-subtle p-2">
            <div className="flex items-start gap-2">
              <span className={`mt-0.5 shrink-0 rounded px-1 text-[10px] ${tone(e)}`}>
                {e.source === 'company' ? t('calendar.company') : '•'}
              </span>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {e.subject || t('calendar.noSubject')}
              </p>
            </div>
            <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 text-xs">
              <dt className="text-slate-500 dark:text-slate-400">{t('calendar.start')}</dt>
              <dd className="text-slate-700 dark:text-slate-200">
                {e.is_all_day ? t('calendar.allDay') : localTimeOfInstant(e.start, offset)}
              </dd>
              {minutes != null && (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('calendar.duration')}</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{formatDuration(minutes)}</dd>
                </>
              )}
              {e.location && (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('calendar.location')}</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{e.location}</dd>
                </>
              )}
              {e.organizer && (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('calendar.organizer')}</dt>
                  <dd className="text-slate-700 dark:text-slate-200">{e.organizer}</dd>
                </>
              )}
            </dl>
            {e.body_preview && (
              <details className="mt-1 text-xs">
                <summary className="cursor-pointer text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100">
                  {t('calendar.showDetails')}
                </summary>
                <p className="mt-1 whitespace-pre-line break-words text-slate-500 dark:text-slate-400">
                  {e.body_preview}
                </p>
              </details>
            )}
          </li>
        )
      })}
    </ul>
  )
}
